import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useOutsideAlerter } from '../hooks/useOutsideAlerter';

type Page = { title: string; route: string };
const pages: Page[] = [
  { title: 'Wünsche', route: '/my-wishes' },
  { title: 'Ideen', route: '/ideas' },
  { title: 'Mit mir geteilt', route: '/shared-with-me' },
];

const NavBar = () => {
  const location = useLocation();
  const { user, logout } = useAppContext();
  const navigate = useNavigate();

  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);

  const userMenuRef = useRef<any>();
  useOutsideAlerter(userMenuRef, () => setUserMenuOpen(false));

  const menuRef = useRef<any>();
  useOutsideAlerter(menuRef, () => setMenuOpen(false));

  const handleClickPage = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, page: Page) => {
    e.preventDefault();
    navigate(page.route);
    setMenuOpen(false);
  };

  return (
    <nav className="bg-gray-900 border-b border-gray-800 fixed w-full z-20 top-0 left-0">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-16">
          {user?.id && (
            <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded="false"
                onClick={() => setMenuOpen((menuOpen) => !menuOpen)}
              >
                <span className="sr-only">Open main menu</span>

                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>

                <svg
                  className="hidden h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          <div className="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start">
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                <path d="M9.375 3a1.875 1.875 0 000 3.75h1.875v4.5H3.375A1.875 1.875 0 011.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0112 2.753a3.375 3.375 0 015.432 3.997h3.943c1.035 0 1.875.84 1.875 1.875v.75c0 1.036-.84 1.875-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 10-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3zM11.25 12.75H3v6.75a2.25 2.25 0 002.25 2.25h6v-9zM12.75 12.75v9h6.75a2.25 2.25 0 002.25-2.25v-6.75h-9z" />
              </svg>
            </div>
            {user?.id && (
              <div className="hidden sm:block sm:ml-6">
                <div className="flex space-x-4">
                  {pages.map((page) => (
                    <a
                      href="/"
                      key={page.title}
                      onClick={(e) => handleClickPage(e, page)}
                      className={
                        location.pathname === page.route
                          ? 'bg-gray-900 text-white px-3 py-2 rounded-md text-sm font-medium'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium'
                      }
                    >
                      {page.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {user?.id && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
              <div className="ml-3 relative">
                <div>
                  <button
                    type="button"
                    className="bg-gray-800 flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                    id="user-menu-button"
                    aria-expanded="false"
                    aria-haspopup="true"
                    onClick={() => setUserMenuOpen((isMenuOpen) => !isMenuOpen)}
                  >
                    <span className="sr-only">Open user menu</span>
                    <span className="flex flex-col h-8 w-8 bg-white rounded-full align-center justify-center">
                      {user.firstname[0]}
                    </span>
                  </button>
                </div>

                {isUserMenuOpen && (
                  <div
                    className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                    tabIndex={-1}
                    ref={userMenuRef}
                  >
                    <a
                      href="/"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/settings');
                        setUserMenuOpen(false);
                      }}
                      className="block px-4 py-2 text-sm text-gray-700"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-1"
                    >
                      Einstellungen
                    </a>
                    {user.role === 'admin' && (
                      <a
                        href="/"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/admin');
                          setUserMenuOpen(false);
                        }}
                        className="block px-4 py-2 text-sm text-gray-700"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-1"
                      >
                        Adminbereich
                      </a>
                    )}

                    <a
                      href="/"
                      className="block px-4 py-2 text-sm text-gray-700"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-2"
                      onClick={() => logout()}
                    >
                      Logout
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isMenuOpen && (
        <div className="sm:hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1" ref={menuRef}>
            {pages.map((page) => (
              <a
                href="/"
                key={page.title}
                className={
                  location.pathname === page.route
                    ? 'bg-gray-900 text-white block px-3 py-2 rounded-md text-base font-medium'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium'
                }
                aria-current="page"
                onClick={(e) => handleClickPage(e, page)}
              >
                {page.title}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
